import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Bell,
  Calendar,
  User,
  Mail,
  FileCheck,
  Clock,
  Upload,
  Paperclip,
  XCircle,
  Edit2,
  Send,
} from 'lucide-react';

import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  Button,
  Card,
  Badge,
  FrequencyBadge,
  StatusBadge,
  TimingBadge,
  LoadingSpinner,
  Modal,
  FormField,
} from '../components/UI';

// -- Safe date formatter ---------------------------------------------
function fmtDate(value, pattern = 'MMM d, yyyy', fallback = '�') {
  if (!value) return fallback;

  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);

    if (isNaN(d.getTime())) return fallback;

    return format(d, pattern);
  } catch {
    return fallback;
  }
}

export default function ReminderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canManageReports } = useAuth();

  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [submitModal, setSubmitModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [submitForm, setSubmitForm] = useState({
    notes: '',
    due_date: '',
  });

  const [files, setFiles] = useState([]);

  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get(`/reminders/${id}`);

      setReminder(res.data);
    } catch (e) {
      toast.error('Failed to load report');
      navigate('/reminders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const canSubmit =
    reminder &&
    (reminder.responsible_user_id === user?.id ||
      reminder.responsible_email === user?.email ||
      canManageReports);

  async function handleSubmit(e) {
    e.preventDefault();

    setSubmitLoading(true);

    try {
      const fd = new FormData();

      fd.append('notes', submitForm.notes);

      if (submitForm.due_date) {
        fd.append('due_date', submitForm.due_date);
      }

      files.forEach((f) => fd.append('attachments', f));

      await api.post(`/reminders/${id}/submit`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Report submitted successfully!');

      setSubmitModal(false);
      setFiles([]);

      setSubmitForm({
        notes: '',
        due_date: '',
      });

      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitLoading(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  if (!reminder) return null;

  const { timeline } = reminder;

  const allEvents = [
    ...((timeline?.logs || [])
      .filter((l) => l.sent_at)
      .map((l) => ({
        ...l,
        kind: 'reminder',
        date: l.sent_at,
      }))),

    ...((timeline?.submissions || [])
      .filter((s) => s.submitted_at)
      .map((s) => ({
        ...s,
        kind: 'submission',
        date: s.submitted_at,
      }))),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const lastSubmission = timeline?.submissions?.[0];

  function parseAttachments(raw) {
    if (!raw) return [];

    return raw
      .split(';;')
      .map((a) => {
        const parts = a.split('|');

        return {
          name: parts[0],
          stored: parts[1],
          size: parts[2],
          mime: parts[3],
        };
      })
      .filter((a) => a.name && a.stored);
  }

  return (
    <div
      style={{
        padding: '32px 36px',
        maxWidth: 1100,
        margin: '0 auto',
      }}
      className="fade-in"
    >
      {/* Back + actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <button
          onClick={() => navigate('/reminders')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: 13.5,
            fontWeight: 600,
          }}
        >
          <ArrowLeft size={16} />
          Back to Reports
        </button>

        <div style={{ display: 'flex', gap: 10 }}>
          {canSubmit && (
            <Button
              icon={FileCheck}
              onClick={() => setSubmitModal(true)}
              variant="success"
            >
              Submit Report
            </Button>
          )}

          {canManageReports && (
            <Button
              icon={Edit2}
              variant="secondary"
              onClick={() => navigate(`/reminders/${id}/edit`)}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Header card */}
      <Card
        style={{
          marginBottom: 20,
          background:
            'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)',
          border: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: 'rgba(255,255,255,.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Bell size={20} color="#fff" />
              </div>

              <div>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#fff',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {reminder.report_name}
                </h1>

                <div
                  style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,.6)',
                    marginTop: 2,
                  }}
                >
                  {reminder.department_name || 'No Department'}
                </div>
              </div>
            </div>

            <p
              style={{
                color: 'rgba(255,255,255,.75)',
                fontSize: 13.5,
                lineHeight: 1.6,
                maxWidth: 600,
              }}
            >
              {reminder.description}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: 'flex-end',
            }}
          >
            <StatusBadge isActive={!!reminder.is_active} />
            <FrequencyBadge frequency={reminder.frequency} />

            {lastSubmission?.submission_timing && (
              <TimingBadge timing={lastSubmission.submission_timing} />
            )}
          </div>
        </div>
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 340px',
          gap: 20,
        }}
      >
        {/* Timeline */}
        <div>
          <Card>
            <h2
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 20,
              }}
            >
              Activity Timeline
            </h2>

            {allEvents.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: 32,
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                No activity yet � reminders and submissions will appear here.
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 19,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: 'var(--border)',
                  }}
                />

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                  }}
                >
                  {allEvents.map((ev, i) => {
                    const isLate =
                      ev.submission_timing === 'after_due';

                    const isSent = ev.status === 'sent';

                    const dotBg =
                      ev.kind === 'submission'
                        ? isLate
                          ? '#fee2e2'
                          : '#dcfce7'
                        : isSent
                        ? '#dbeafe'
                        : '#fee2e2';

                    const dotBorder =
                      ev.kind === 'submission'
                        ? isLate
                          ? 'var(--danger)'
                          : 'var(--success)'
                        : isSent
                        ? 'var(--accent)'
                        : 'var(--danger)';

                    const attachments =
                      ev.kind === 'submission'
                        ? parseAttachments(ev.attachments)
                        : [];

                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 16,
                          paddingBottom: 24,
                          position: 'relative',
                        }}
                      >
                        {/* Timeline dot */}
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            flexShrink: 0,
                            zIndex: 1,
                            background: dotBg,
                            border: `2px solid ${dotBorder}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {ev.kind === 'submission' ? (
                            <FileCheck
                              size={16}
                              color={
                                isLate
                                  ? 'var(--danger)'
                                  : 'var(--success)'
                              }
                            />
                          ) : isSent ? (
                            <Send
                              size={15}
                              color="var(--accent)"
                            />
                          ) : (
                            <XCircle
                              size={15}
                              color="var(--danger)"
                            />
                          )}
                        </div>

                        {/* Event content */}
                        <div style={{ flex: 1, paddingTop: 8 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 13.5,
                                color: 'var(--text-primary)',
                              }}
                            >
                              {ev.kind === 'submission'
                                ? 'Report Submitted'
                                : 'Reminder Sent'}
                            </span>

                            {ev.kind === 'submission' &&
                              ev.submission_timing && (
                                <TimingBadge
                                  timing={ev.submission_timing}
                                />
                              )}

                            {ev.kind === 'reminder' &&
                              ev.status && (
                                <Badge
                                  label={ev.status}
                                  type={
                                    isSent
                                      ? 'success'
                                      : 'danger'
                                  }
                                />
                              )}
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--text-muted)',
                              marginBottom: 6,
                            }}
                          >
                            {fmtDate(
                              ev.date,
                              'EEEE, MMMM d yyyy � h:mm a'
                            )}
                          </div>

                          {ev.kind === 'submission' && (
                            <div
                              style={{
                                fontSize: 13,
                                color: 'var(--text-secondary)',
                              }}
                            >
                              {ev.submitted_by_name && (
                                <span>
                                  Submitted by{' '}
                                  <strong>
                                    {ev.submitted_by_name}
                                  </strong>
                                </span>
                              )}

                              {ev.due_date && (
                                <span>
                                  {' '}
                                  � Due:{' '}
                                  <strong>
                                    {fmtDate(
                                      ev.due_date,
                                      'MMM d, yyyy'
                                    )}
                                  </strong>
                                </span>
                              )}
                            </div>
                          )}

                          {ev.kind === 'reminder' &&
                            ev.recipients && (
                              <div
                                style={{
                                  fontSize: 12.5,
                                  color:
                                    'var(--text-secondary)',
                                }}
                              >
                                To: {ev.recipients}
                              </div>
                            )}

                          {ev.notes && (
                            <div
                              style={{
                                marginTop: 8,
                                padding: '8px 12px',
                                background:
                                  'var(--bg-elevated)',
                                borderRadius: 8,
                                fontSize: 12.5,
                                color:
                                  'var(--text-secondary)',
                                fontStyle: 'italic',
                                borderLeft:
                                  '3px solid var(--border)',
                              }}
                            >
                              "{ev.notes}"
                            </div>
                          )}

                          {attachments.length > 0 && (
                            <div
                              style={{
                                marginTop: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                              }}
                            >
                              {attachments.map((att, ai) => (
                                <a
                                  key={ai}
                                  href={`${
                                    process.env
                                      .REACT_APP_API_URL ||
                                    'http://localhost:4000/api'
                                  }/reminders/attachment-file/${
                                    att.stored
                                  }`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 10px',
                                    background:
                                      'var(--primary-ghost)',
                                    borderRadius: 7,
                                    fontSize: 12,
                                    color: 'var(--primary)',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                  }}
                                >
                                  <Paperclip size={12} />

                                  {att.name}

                                  {att.size && (
                                    <span
                                      style={{
                                        marginLeft: 'auto',
                                        color:
                                          'var(--text-muted)',
                                        fontWeight: 400,
                                        fontSize: 11,
                                      }}
                                    >
                                      {Math.round(
                                        Number(att.size) / 1024
                                      )}{' '}
                                      KB
                                    </span>
                                  )}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Details */}
          <Card>
            <h3
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 16,
              }}
            >
              Report Details
            </h3>

            {[
              {
                icon: User,
                label: 'Responsible',
                value: reminder.responsible_person,
              },
              {
                icon: Mail,
                label: 'Email',
                value: reminder.responsible_email,
              },
              {
                icon: Calendar,
                label: 'Next Due',
                value: fmtDate(reminder.next_due_date),
              },
              {
                icon: Clock,
                label: 'Next Reminder',
                value: fmtDate(
                  reminder.next_reminder_date
                ),
              },
              {
                icon: Calendar,
                label: 'Expires',
                value: fmtDate(reminder.expiry_date),
              },
              {
                icon: Bell,
                label: 'Lead Days',
                value: `${reminder.reminder_lead_days} day${
                  reminder.reminder_lead_days !== 1
                    ? 's'
                    : ''
                }`,
              },
            ].map(({ icon: Icon, label, value }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: 'var(--primary-ghost)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={13} color="var(--primary)" />
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '.04em',
                    }}
                  >
                    {label}
                  </div>

                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      marginTop: 1,
                    }}
                  >
                    {value || '�'}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {/* Submit Modal */}
      <Modal
        open={submitModal}
        onClose={() => setSubmitModal(false)}
        title="Submit Report"
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setSubmitModal(false)}
            >
              Cancel
            </Button>

            <Button
              variant="success"
              icon={FileCheck}
              loading={submitLoading}
              onClick={handleSubmit}
            >
              Confirm Submission
            </Button>
          </>
        }
      >
        <div>
          <div
            style={{
              padding: '12px 16px',
              background: 'var(--bg-elevated)',
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 13.5,
              color: 'var(--text-secondary)',
            }}
          >
            Submitting:{' '}
            <strong>{reminder.report_name}</strong>

            {reminder.next_due_date && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: 'var(--text-muted)',
                }}
              >
                Next due:{' '}
                {fmtDate(
                  reminder.next_due_date,
                  'MMMM d, yyyy'
                )}
              </div>
            )}
          </div>

          <FormField
            label="Due Date"
            hint="Leave blank to use the calculated next due date"
          >
            <input
              type="date"
              value={submitForm.due_date}
              onChange={(e) =>
                setSubmitForm((f) => ({
                  ...f,
                  due_date: e.target.value,
                }))
              }
            />
          </FormField>

          <FormField label="Notes / Comments">
            <textarea
              value={submitForm.notes}
              onChange={(e) =>
                setSubmitForm((f) => ({
                  ...f,
                  notes: e.target.value,
                }))
              }
              placeholder="Any comments about this submission..."
              rows={3}
            />
          </FormField>

          <FormField
            label="Evidence / Attachments"
            hint="Max 5 files, 10MB each"
          >
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: '2px dashed var(--border)',
                borderRadius: 10,
                padding: '20px 16px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <Upload
                size={22}
                color="var(--text-muted)"
                style={{ marginBottom: 8 }}
              />

              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                }}
              >
                {files.length > 0
                  ? `${files.length} file(s) selected`
                  : 'Click to upload files'}
              </div>

              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.zip"
                style={{ display: 'none' }}
                onChange={(e) =>
                  setFiles(
                    Array.from(e.target.files).slice(0, 5)
                  )
                }
              />
            </div>
          </FormField>
        </div>
      </Modal>
    </div>
  );
}